/**
 * @fileoverview Syndicator Showdown — Admin Odds Entry Page
 * @module pages/admin/AdminOddsPage
 *
 * Tabs: ALL | L1 · Venue R5 | L2 · Venue R6 | …
 * Tab colour: green = all active runners have odds > 1, red = incomplete.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection, query, orderBy,
  onSnapshot, doc, writeBatch, getDoc, updateDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db }           from '../../services/firebase';
import { calculateBaseSharePrice } from '@ss/shared';

const recalculateRaceValuesForSlateFn = httpsCallable(
  getFunctions(undefined, 'australia-southeast1'), 'recalculateRaceValuesForSlate'
);
const refreshValueHealthWarningFn = httpsCallable(
  getFunctions(undefined, 'australia-southeast1'), 'refreshValueHealthWarning'
);
import './AdminOddsPage.css';

// ─── Tab completion helper ────────────────────────────────────────────────────

/**
 * A race tab is GREEN (complete) when every non-scratched runner has valid odds.
 * Returns 'complete' | 'incomplete' | 'empty' (no runners at all).
 */
function raceOddsStatus(race) {
  const runners = race.runners || [];
  if (runners.length === 0) return 'empty';
  const active = runners.filter(r => r.status?.toLowerCase() !== 'scratched');
  if (active.length === 0) return 'complete'; // all scratched — nothing to enter
  const allHaveOdds = active.every(r => {
    const v = parseFloat(r.odds);
    return !isNaN(v) && v > 1;
  });
  return allHaveOdds ? 'complete' : 'incomplete';
}

// ─── Race Tab Bar ─────────────────────────────────────────────────────────────

/**
 * Renders the ALL tab + one tab per leg.
 * localOddsMap: { [raceId]: { [runnerId]: string } } — unsaved in-memory odds
 * used to compute completion status optimistically while typing.
 */
const RaceTabBar = ({ races, activeTab, onTabChange, localOddsMap }) => {
  return (
    <div className="ss-odds-tab-bar">
      {/* ALL tab — always neutral */}
      <button
        type="button"
        className={`ss-odds-tab ${activeTab === 'ALL' ? 'ss-odds-tab--active' : ''}`}
        onClick={() => onTabChange('ALL')}
      >
        All
      </button>

      {races.map((race, index) => {
        // Merge saved odds with any unsaved local edits for completion calc
        const localOdds = localOddsMap[race.id] || {};
        const raceWithLocalOdds = {
          ...race,
          runners: (race.runners || []).map(r => ({
            ...r,
            odds: localOdds[r.id] !== undefined
              ? parseFloat(localOdds[r.id])
              : r.odds,
          })),
        };

        const status  = raceOddsStatus(raceWithLocalOdds);
        const legNum  = index + 1;
        // Use meetingName from race if available, strip to first word for brevity
        const venue   = race.meetingName
          ? race.meetingName.split(' ')[0]
          : '';
        const label   = venue
          ? `L${legNum} · ${venue} R${race.raceNumber}`
          : `L${legNum} · R${race.raceNumber}`;

        return (
          <button
            key={race.id}
            type="button"
            className={[
              'ss-odds-tab',
              activeTab === race.id ? 'ss-odds-tab--active'    : '',
              status === 'complete'  ? 'ss-odds-tab--complete'  : '',
              status === 'incomplete'? 'ss-odds-tab--incomplete': '',
            ].filter(Boolean).join(' ')}
            onClick={() => onTabChange(race.id)}
            title={`Leg ${legNum}: ${race.meetingName || ''} Race ${race.raceNumber}${status === 'complete' ? ' ✓' : ''}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

// ─── Runner odds row ──────────────────────────────────────────────────────────

const RunnerOddsRow = ({ runner, odds, onOddsChange, onScratch, raceCount, fieldSize, avgSpendPerRace = 100000, kMultiplier = 2.900 }) => {
  const currentOdds = odds ?? runner.odds ?? '';
  const numOdds     = parseFloat(currentOdds);

  const price = !isNaN(numOdds) && numOdds > 1
    ? calculateBaseSharePrice(numOdds, fieldSize || 12, raceCount || 8, avgSpendPerRace, kMultiplier)
    : null;

  const isScratched = runner.status?.toLowerCase() === 'scratched';

  return (
    <div className={`ss-odds-runner-row ${isScratched ? 'ss-odds-runner-row--scratched' : ''}`}>
      <div className="ss-odds-runner-num">{runner.runnerNumber}</div>
      <div className="ss-odds-runner-info">
        <span className="ss-odds-runner-name">{runner.name}</span>
        {runner.jockey && (
          <span className="ss-odds-runner-meta">
            {runner.barrier ? `B${runner.barrier} · ` : ''}{runner.jockey}
          </span>
        )}
      </div>
      {isScratched ? (
        <div className="ss-odds-scratched-tag">SCRATCHED</div>
      ) : (
        <>
          <div className="ss-odds-input-wrap">
            <span className="ss-odds-dollar-prefix">$</span>
            <input
              type="number"
              className="ss-odds-input"
              value={currentOdds}
              onChange={e => onOddsChange(runner.id, e.target.value)}
              placeholder="4.50"
              min="1.01"
              max="999"
              step="0.50"
            />
          </div>
          <div className={`ss-odds-price-preview ${price ? '' : 'ss-odds-price-preview--empty'}`}>
            {price
              ? <><span className="ss-odds-price-label">$/1%</span>
                  <span className="ss-odds-price-val">${price.toLocaleString('en-AU')}</span></>
              : <span className="ss-odds-price-hint">Enter odds</span>
            }
          </div>
        </>
      )}
      <button
        onClick={() => onScratch && onScratch(runner.id)}
        title={isScratched ? 'Click to reinstate' : 'Scratch this runner'}
        style={{
          background:   isScratched ? '#9ca3af' : '#f59e0b',
          color:        isScratched ? '#fff' : '#1a1a2e',
          border:       'none',
          borderRadius: '6px',
          padding:      '5px 10px',
          fontSize:     '11px',
          fontWeight:   '700',
          cursor:       'pointer',
          minWidth:     '44px',
          whiteSpace:   'nowrap',
          flexShrink:   0,
        }}
      >
        {isScratched ? '✕ SCR' : 'SCR'}
      </button>
    </div>
  );
};

// ─── Race odds card ───────────────────────────────────────────────────────────

const RaceOddsCard = ({ race, raceCount, slateId, avgSpendPerRace = 100000, kMultiplier = 2.900, onSave, onLocalOddsChange }) => {
  const [localOdds, setLocalOdds] = useState(() => {
    const initial = {};
    (race.runners || []).forEach(r => {
      if (r.odds) initial[r.id] = String(r.odds);
    });
    return initial;
  });
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [modified, setModified] = useState(false);

  // Keep parent's localOddsMap in sync for tab completion colouring
  useEffect(() => {
    onLocalOddsChange?.(race.id, localOdds);
  }, [localOdds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-seed local odds when race data changes from Firestore
  useEffect(() => {
    setLocalOdds(() => {
      const initial = {};
      (race.runners || []).forEach(r => {
        if (r.odds) initial[r.id] = String(r.odds);
      });
      return initial;
    });
    setModified(false);
    setSaved(false);
  }, [race.id]); // only re-seed when race ID changes, not on every runner update

  const handleOddsChange = useCallback((runnerId, value) => {
    setLocalOdds(prev => ({ ...prev, [runnerId]: value }));
    setModified(true);
    setSaved(false);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(race.id, localOdds);
      setSaved(true);
      setModified(false);
    } catch (err) {
      console.error('Failed to save odds:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleScratch = async (runnerId) => {
    const raceRef  = doc(db, 'game_slates', slateId, 'races', race.id);
    const raceSnap = await getDoc(raceRef);
    if (!raceSnap.exists()) return;
    const updatedRunners = (raceSnap.data().runners || []).map(r =>
      r.id === runnerId ? { ...r, scratched: !r.scratched, status: r.scratched ? 'active' : 'scratched' } : r
    );
    await updateDoc(raceRef, { runners: updatedRunners });
  };

  const activeRunners = (race.runners || []).filter(r => r.status?.toLowerCase() !== 'scratched');
  const allOddsEntered = activeRunners.every(r => {
    const v = parseFloat(localOdds[r.id]);
    return !isNaN(v) && v > 1;
  });

  return (
    <div className="ss-odds-race-card">
      <div className="ss-odds-race-header">
        <div>
          <span className="ss-odds-race-badge">R{race.raceNumber}</span>
          <span className="ss-odds-race-name">{race.raceName || `Race ${race.raceNumber}`}</span>
          {race.distance && <span className="ss-odds-race-meta">{race.distance}m</span>}
        </div>
        <div className="ss-odds-race-actions">
          {allOddsEntered && activeRunners.length > 0 && (
            <span className="ss-odds-complete-badge">✓ All odds entered</span>
          )}
        </div>
      </div>

      <div className="ss-odds-runner-header">
        <span>#</span>
        <span>Runner</span>
        <span>Market odds</span>
        <span>Share price / 1%</span>
      </div>

      {(race.runners || [])
        .sort((a, b) => (a.runnerNumber || 0) - (b.runnerNumber || 0))
        .map(runner => (
          <RunnerOddsRow
            key={runner.id}
            runner={runner}
            odds={localOdds[runner.id]}
            onOddsChange={handleOddsChange}
            onScratch={handleScratch}
            raceCount={raceCount}
            fieldSize={activeRunners.length}
            avgSpendPerRace={avgSpendPerRace}
            kMultiplier={kMultiplier}
          />
        ))
      }

      <div className="ss-odds-save-bar">
        {saved && !modified && <span className="ss-odds-saved-msg">✓ Odds saved</span>}
        {modified && <span className="ss-odds-unsaved-msg">Unsaved changes</span>}
        <button
          className={`ss-odds-save-btn ${!modified ? 'ss-odds-save-btn--dim' : ''}`}
          onClick={handleSave}
          disabled={saving || !modified}
          type="button"
        >
          {saving ? '⏳ Saving…' : 'Save Odds'}
        </button>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const AdminOddsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const slateId = searchParams.get('slate') || '';

  const [currentUser,     setCurrentUser]     = useState(null);
  const [slates,          setSlates]          = useState([]);
  const [races,           setRaces]           = useState([]);
  const [globalStatus,    setGlobalStatus]    = useState(null);
  const [avgSpendPerRace, setAvgSpendPerRace] = useState(100000);
  const [kMultiplier,     setKMultiplier]     = useState(2.900);
  const [activeTab,       setActiveTab]       = useState('ALL');
  const [pricesLocked,    setPricesLocked]    = useState(false);
  const [lockingPrices,   setLockingPrices]   = useState(false);

  // Tracks unsaved local odds per race for optimistic tab colouring
  // Shape: { [raceId]: { [runnerId]: string } }
  const [localOddsMap, setLocalOddsMap] = useState({});

  useEffect(() => { return onAuthStateChanged(auth, setCurrentUser); }, []);

  useEffect(() => {
    const q = query(collection(db, 'game_slates'), orderBy('meetingDate', 'desc'));
    return onSnapshot(q, snap =>
      setSlates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, []);

  // Active (non-ended, non-archived) slates only — filter client-side
  const activeSlates = useMemo(() =>
    slates
      .filter(s =>
        s.finalised !== true &&
        s.status !== 'finalised' &&
        s.status !== 'completed' &&
        s.status !== 'resulted' &&
        s.archived !== true
      )
      .sort((a, b) => (b.meetingDate || '').localeCompare(a.meetingDate || ''))
  , [slates]);

  // Auto-select first active slate when the current selection is no longer active
  useEffect(() => {
    if (activeSlates.length === 0) return;
    if (slateId && activeSlates.find(s => s.id === slateId)) return;
    setSearchParams({ slate: activeSlates[0].id });
  }, [activeSlates, slateId]);

  useEffect(() => {
    if (!slateId) { setRaces([]); setAvgSpendPerRace(100000); setKMultiplier(2.900); setActiveTab('ALL'); return; }

    const slateUnsub = onSnapshot(doc(db, 'game_slates', slateId), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setAvgSpendPerRace(data?.avgSpendPerRace || 100000);
        setKMultiplier(data?.kMultiplier ?? 2.900);
        setPricesLocked(!!data?.pricesLocked);
      }
    });

    const q = query(
      collection(db, 'game_slates', slateId, 'races'),
      orderBy('raceNumber', 'asc')
    );
    const raceUnsub = onSnapshot(q, snap => {
      setRaces(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    setActiveTab('ALL');
    setLocalOddsMap({});

    return () => { slateUnsub(); raceUnsub(); };
  }, [slateId]);

  const handleSaveOdds = useCallback(async (raceId, oddsMap) => {
    const raceRef  = doc(db, 'game_slates', slateId, 'races', raceId);
    const raceSnap = await getDoc(raceRef);
    if (!raceSnap.exists()) return;

    const raceData = raceSnap.data();
    const updatedRunners = (raceData.runners || []).map(runner => ({
      ...runner,
      odds: oddsMap[runner.id]
        ? parseFloat(oddsMap[runner.id])
        : (runner.odds || null),
    }));

    const batch = writeBatch(db);
    batch.update(raceRef, { runners: updatedRunners });
    await batch.commit();

    setGlobalStatus(`✓ Odds saved for R${raceData.raceNumber}`);
    setTimeout(() => setGlobalStatus(null), 3000);
  }, [slateId]);

  // Called by each RaceOddsCard as the user types, keeps localOddsMap fresh
  const handleLocalOddsChange = useCallback((raceId, oddsMap) => {
    setLocalOddsMap(prev => ({ ...prev, [raceId]: oddsMap }));
  }, []);

  const handleLockPrices = useCallback(async () => {
    // Hard block: never let a game go live off stale/thin ratings. Checked
    // BEFORE the confirm dialog so a distracted admin can't click through it.
    const preCheckSnap = await getDoc(doc(db, 'game_slates', slateId));
    const preCheckData = preCheckSnap.data();
    if (preCheckData?.ratingsStatus === 'STALE_OR_THIN_DATA') {
      setGlobalStatus(
        '❌ Blocked: ratings are flagged STALE_OR_THIN_DATA (more than half the field has little/no ' +
        'real form data). Run "♻ Refresh Form + Ratings" on the Slates page before locking prices.'
      );
      setTimeout(() => setGlobalStatus(null), 8000);
      return;
    }

    const confirmed = window.confirm(
      'Lock share prices?\n\n' +
      'Players will be able to see prices and enter the game. ' +
      'Prices are frozen from this point — you can still adjust odds but players ' +
      'will see the prices from the time of locking.'
    );
    if (!confirmed) return;
    setLockingPrices(true);
    try {
      // Required lifecycle: ratings calculated -> share prices generated ->
      // value_engine_v1 runs -> value fields written -> THEN prices locked.
      // This persists sharePrice/valueLabel/valueBadge etc. onto every
      // runner from the odds just entered, before players ever see a price.
      setGlobalStatus('⏳ Generating share prices + value labels…');
      const { data: valueResult } = await recalculateRaceValuesForSlateFn({ slateId });

      const gameRef  = doc(db, 'game_slates', slateId);
      const gameSnap = await getDoc(gameRef);
      const gameData = gameSnap.data();

      const now     = Timestamp.now();
      const payload = {
        pricesLocked:   true,
        pricesLockedAt: now,
      };

      // If gameOpenTime is still in the future, advance it to now so the
      // game immediately shows as LIVE rather than "OPENING SOON".
      const openMs = gameData?.gameOpenTime?.toMillis?.() ?? 0;
      if (openMs > Date.now()) {
        payload.gameOpenTime  = now;
        payload.forceOpenedAt = now;
      }

      await updateDoc(gameRef, payload);

      // Re-check the health guard now that pricesLocked is actually true —
      // valueResult.warning above was necessarily computed BEFORE the lock
      // write and would always read null; this is the real post-lock check.
      const { data: healthResult } = await refreshValueHealthWarningFn({ slateId });

      if (healthResult?.warning) {
        setGlobalStatus('⚠️ Prices locked, but: ' + healthResult.warning);
        setTimeout(() => setGlobalStatus(null), 10000);
      } else {
        setGlobalStatus(`✅ Prices locked (${valueResult?.racesUpdated ?? 0} race(s) valued) — players can now enter`);
        setTimeout(() => setGlobalStatus(null), 5000);
      }
    } catch (err) {
      console.error('Failed to lock prices:', err);
      setGlobalStatus('❌ Lock failed: ' + err.message);
      setTimeout(() => setGlobalStatus(null), 8000);
    } finally {
      setLockingPrices(false);
    }
  }, [slateId]);

  // Write oddsComplete flag to game_slates so the admin dashboard
  // knows whether it's safe to auto-open the game
  useEffect(() => {
    if (!slateId || races.length === 0) return;

    const allComplete = races.every(race => {
      const localOdds = localOddsMap[race.id] || {};
      const active = (race.runners || []).filter(r => r.status?.toLowerCase() !== 'scratched');
      if (active.length === 0) return true;
      return active.every(r => {
        const v = parseFloat(localOdds[r.id] ?? r.odds);
        return !isNaN(v) && v > 1;
      });
    });

    updateDoc(doc(db, 'game_slates', slateId), {
      oddsComplete: allComplete,
    }).catch(err => console.warn('oddsComplete write failed:', err));
  }, [races, localOddsMap, slateId]);

  // Which races to render — ALL or filtered to the active tab
  const visibleRaces = useMemo(() =>
    activeTab === 'ALL' ? races : races.filter(r => r.id === activeTab)
  , [races, activeTab]);

  // Summary counts for the progress line
  const completeCount = useMemo(() =>
    races.filter(r => {
      const localOdds = localOddsMap[r.id] || {};
      const merged = {
        ...r,
        runners: (r.runners || []).map(run => ({
          ...run,
          odds: localOdds[run.id] !== undefined ? parseFloat(localOdds[run.id]) : run.odds,
        })),
      };
      return raceOddsStatus(merged) === 'complete';
    }).length
  , [races, localOddsMap]);

  const raceCount = races.length || 8;

  if (!currentUser) {
    return <div className="ss-odds-page"><div className="ss-odds-auth">Admin sign-in required.</div></div>;
  }

  return (
    <div className="ss-odds-page">
      <div className="ss-odds-page-header">
        <div>
          <h1 className="ss-odds-page-title">Odds Entry</h1>
          <p className="ss-odds-page-sub">
            Enter current market odds for each runner. Share prices calculate automatically.
          </p>
        </div>
      </div>

      <div className="ss-odds-instructions">
        <strong>How it works:</strong> Enter decimal odds (e.g. $4.50, $12.00) for each runner.
        The base share price per 1% equity is calculated from the odds and the average spend per race
        (${avgSpendPerRace.toLocaleString('en-AU')}). Update any time before price lock. After lock, odds are frozen.
      </div>

      <div className="ss-odds-selector-wrap">
        <label className="ss-odds-selector-label">Select game</label>
        <select
          className="ss-odds-selector"
          value={slateId}
          onChange={e => setSearchParams({ slate: e.target.value })}
        >
          <option value="">— Choose a game —</option>
          {activeSlates.map(s => (
            <option key={s.id} value={s.id}>
              {s.gameName || s.id} · {s.meetingDate}
            </option>
          ))}
        </select>
      </div>

      {globalStatus && (
        <div className="ss-odds-global-status">{globalStatus}</div>
      )}

      {slateId && races.length > 0 && (
        <>
          {/* Progress summary + lock prices action */}
          <div className="ss-odds-progress-bar">
            <div className="ss-odds-progress">
              {pricesLocked
                ? <span className="ss-odds-locked-badge">🔒 Prices Locked — Players can now enter</span>
                : completeCount === races.length
                  ? <span className="ss-odds-complete">✓ All races have odds — ready to lock prices</span>
                  : <span>{completeCount} of {races.length} races complete</span>
              }
            </div>
            {!pricesLocked && completeCount === races.length && (
              <button
                className="ss-odds-lock-btn"
                onClick={handleLockPrices}
                disabled={lockingPrices}
                type="button"
              >
                {lockingPrices ? '⏳ Locking…' : '🔒 Lock Prices & Open to Players'}
              </button>
            )}
          </div>

          {/* Tab bar */}
          <RaceTabBar
            races={races}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            localOddsMap={localOddsMap}
          />

          {/* Race cards — filtered by active tab */}
          {visibleRaces.map(race => (
            <RaceOddsCard
              key={race.id}
              race={race}
              raceCount={raceCount}
              slateId={slateId}
              avgSpendPerRace={avgSpendPerRace}
              kMultiplier={kMultiplier}
              onSave={handleSaveOdds}
              onLocalOddsChange={handleLocalOddsChange}
            />
          ))}
        </>
      )}

      {slateId && races.length === 0 && (
        <div className="ss-odds-empty">
          No races found for this game. Add races via the Setup tab first.
        </div>
      )}
    </div>
  );
};

export default AdminOddsPage;
